import { Injectable, OnDestroy } from '@angular/core';
import { LoggerService } from 'src/app/core/logger.service';
import { UserService } from 'src/app/core/user.service';
import { PermissionActions, PlanType } from '../models/enums';
import { BroadcastService } from './broadcast.service';
import { PlanTerm } from '../models/api/plandetails.model';
import { Subscription } from 'rxjs';
import { CAccountInfo } from '../models/api/chargbeeprofile-api.model';
import { ChargebeeService } from './chargebee.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../reducers';
import { User } from '../models';

@Injectable({
  providedIn: 'root',
})
export class SkuService implements OnDestroy {
  public permissionActions = PermissionActions;
  public cprofile: CAccountInfo;
  public sku: string;
  public planLimit: number;
  private chargebeeSubscription: Subscription;
  private userSubscription: Subscription;
  private user: User;

  private purgeDataLimits: { [key in PlanType]: number } = {
    [PlanType.FREE]: 30,
    [PlanType.PER]: 180,
    [PlanType.LPER]: 180,
    [PlanType.PS]: 180,
    [PlanType.PSP]: 365,
    [PlanType.BPT]: 180,
    [PlanType.BPTU]: 365,
    [PlanType.BPTSP]: 180,
    [PlanType.BPTUP]: 365,
  };

  constructor(
    private userService: UserService,
    private log: LoggerService,
    private broadcastService: BroadcastService,
    private chargebeeService: ChargebeeService,
    private store: Store<fromRoot.State>,
  ) {
    this.broadcastService.on('sync.reset.user').subscribe(() => {
      this.sku = '';
      this.cprofile = null;
      this.planLimit = null;
    });
    this.broadcastService.on('sync.sessionnew.user').subscribe(() => {
      this.sku = '';
      this.cprofile = null;
      this.planLimit = null;
    });
    this.userSubscription = this.store.select(fromRoot.getAuthState).subscribe((data) => {
      if (!data.authenticated) {
        this.user = new User();
        return;
      }
      if (data.user) {
        this.user = data.user;
        this.sku = this.user.plan_sku || 'free';
      }
    });
  }

  public ngOnDestroy(): void {
    if (this.chargebeeSubscription) {
      this.chargebeeSubscription.unsubscribe();
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  public async getSku(): Promise<string> {
    if (this.sku) {
      return this.sku;
    }

    if (!this.userService.isAuthenticated()) {
      return '';
    }

    this.sku = this.userService.get('plan_sku') || 'free';

    if (this.sku === 'free') {
      this.log.info('Sku not found, defaulting to free');
    }
    return this.sku;
  }

  // Function to tell if teams+ user using sku
  public isTeamsPlusUser(sku: string): boolean {
    // expect the sku string as it can be reused at other places as well
    return sku ? sku.includes('BPTSP') || (sku.includes('BPTUP') as boolean) : false;
  }

  // Function Add to get the Product Plan SKU value product code like PSP-6T-Y-1 and we get PSP only.
  public async getProductPlanSKU(sku?: string): Promise<string> {
    if (!sku) {
      sku = await this.getSku();
    }
    return sku.split('-')[0];
  }

  // Function Add to get the  Plan Term value product code like PSP-6T-Y-1 and we get M or Y only.
  public getPlanTerm(plan?: string): string {
    let term = '';
    let planTerm = '';

    if (plan.split('-').length > 2) {
      term = plan.split('-')[2];
    }

    switch (term) {
      case 'M':
        planTerm = PlanTerm.MONTHLY;
        break;
      case 'Y':
        planTerm = PlanTerm.ANNUAL;
        break;
      default:
        planTerm = PlanTerm.ANNUAL;
        this.log.info('Could not determine plan term, defaulting to yearly');
        break;
    }

    return planTerm;
  }

  public async getPlanType(sku?: string): Promise<string> {
    const productPlan = await this.getProductPlanSKU(sku);
    const planType = PlanType[productPlan] || 'free';
    return planType;
  }

  public async isFreeUser(): Promise<boolean> {
    const isTeamsPlusUser =
      this.userService.get('is_multi_new') && this.userService.get('is_unlimited_plan') ? true : false;
    return (await this.getSku()) == PlanType.FREE && !isTeamsPlusUser;
  }

  public async getPanLimit(): Promise<number> {
    if (this.planLimit) {
      return this.planLimit;
    }
    if (this.userService.isAuthenticated()) {
      await new Promise((resolve) => {
        this.chargebeeSubscription = this.chargebeeService.getSubscription().subscribe((data) => {
          if (data.isLoaded) {
            this.cprofile = data.cprofile;
            this.planLimit = this.cprofile.subscription.user_limit;
            resolve(true);
          }
        });
      });
      return this.planLimit;
    } else {
      return 1;
    }
  }

  public async getDataLimit(): Promise<number> {
    const planType = await this.getPlanType();

    const purgeLimit = this.purgeDataLimits[planType];

    if (!purgeLimit) {
      this.log.warn(`Plan type ${planType} not found. Defaulting purge limit to 30 days.`);
      return this.purgeDataLimits[PlanType.FREE];
    }

    return purgeLimit;
  }
}
